import axios from "axios";
import { API_BASE_URL } from "../config";

let accessToken = null;
let refreshInFlight = null;
let sessionExpiredHandler = () => {};

const isApiRequest = (url = "") => String(url).startsWith(API_BASE_URL);
const isAuthEndpoint = (url = "") => ["/login/", "/auth/refresh", "/auth/logout"]
    .some((suffix) => String(url).endsWith(suffix));

export const decodeAccessToken = (token) => {
    try {
        const [, payload] = token.split(".");
        if (!payload) return null;
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), "=");
        return JSON.parse(atob(padded));
    } catch (error) {
        return null;
    }
};

export const setAccessToken = (token) => {
    accessToken = token || null;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
    accessToken = null;
    // Remove credentials created by the previous localStorage-based flow.
    localStorage.removeItem("token");
    localStorage.removeItem("user_login");
};

export const setSessionExpiredHandler = (handler) => {
    sessionExpiredHandler = typeof handler === "function" ? handler : () => {};
};

export const refreshAccessToken = async () => {
    if (!refreshInFlight) {
        refreshInFlight = axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
            withCredentials: true,
            skipAuthRefresh: true,
        }).then((response) => {
            setAccessToken(response.data.access_token);
            return response.data.access_token;
        }).finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

export const restoreSession = async () => {
    const token = await refreshAccessToken();
    const payload = decodeAccessToken(token);
    if (!payload?.sub) throw new Error("Refresh response has no user identity");
    return { developer_login: payload.sub };
};

export const logoutSession = async () => {
    try {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
            withCredentials: true,
            skipAuthRefresh: true,
        });
    } finally {
        clearAccessToken();
    }
};

// A single interceptor covers both old API helpers and direct axios calls.
if (axios.interceptors?.request && axios.interceptors?.response) {
    axios.interceptors.request.use((config) => {
        if (isApiRequest(config.url)) {
            config.withCredentials = true;
            if (accessToken) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const config = error?.config;
            if (
                error?.response?.status !== 401 ||
                !config ||
                config.skipAuthRefresh ||
                config._authRetried ||
                !isApiRequest(config.url) ||
                isAuthEndpoint(config.url)
            ) {
                return Promise.reject(error);
            }

            config._authRetried = true;
            try {
                const token = await refreshAccessToken();
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${token}`;
                config.withCredentials = true;
                return axios(config);
            } catch (refreshError) {
                clearAccessToken();
                sessionExpiredHandler();
                return Promise.reject(error);
            }
        },
    );
}
